import { css, ThemeProvider } from '@emotion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';

import { CountdownOverlay } from './countdown-overlay';
import { DebugConsole } from './debug-console';
import { IOSAudioUnlock } from './ios-audio-unlock';
import { SectionDisplay } from './section-display';
import { SettingsPanel } from './settings-panel';
import { SyncStatusIndicator } from './sync-status-indicator';
import { TeleprompterControls } from './teleprompter-controls';

import type { AudioSectionInput } from '@/hooks/use-audio-player';
import { useAudioPlayer } from '@/hooks/use-audio-player';
import { useAutoScroll } from '@/hooks/use-auto-scroll';
import { useCountdown } from '@/hooks/use-countdown';
import { useFollowDebug } from '@/hooks/use-follow-debug';
import { useFollowerSync } from '@/hooks/use-follower-sync';
import type { SessionSettings } from '@/hooks/use-join-session';
import { useTeleprompterSettings } from '@/hooks/use-teleprompter-settings';
import { playBeep } from '@/lib/audio/beep-generator';
import { isIOSSafari } from '@/lib/utils/detect-ios';
import { darkTheme } from '@/theme/dark-theme';
import { mq, spacing } from '@/theme/tokens';

export interface TeleprompterSection {
  title: string | null;
  content: string;
  position: number;
  speakerNotes?: string[];
}

export interface TeleprompterViewProps {
  sections: TeleprompterSection[];
  audioSections: AudioSectionInput[];
  socket: Socket | null;
  sessionId: string | null;
  sessionSettings: SessionSettings;
}

export function TeleprompterView({
  sections,
  audioSections,
  socket,
  sessionId,
  sessionSettings,
}: TeleprompterViewProps) {
  const [audioUnlocked, setAudioUnlocked] = useState(!isIOSSafari());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    settings,
    setFontSize,
    setLineWidth,
    setScrollSpeed,
    setMusicVolume,
    setAutoScroll,
    setMirrorHorizontal,
    setMirrorVertical,
    resetDefaults,
  } = useTeleprompterSettings();
  const settingsKey = settings.fontSize + settings.lineWidth;

  const audioPlayer = useAudioPlayer(audioSections);
  const hasAudio = audioSections.length > 0;

  // Apply volume setting to audio player
  useEffect(() => {
    audioPlayer.setVolume(settings.musicVolume);
  }, [audioPlayer, settings.musicVolume]);

  const countdown = useCountdown({
    onComplete: () => audioPlayer.play(),
    onTick: () => playBeep(),
  });

  const sectionDurations = useMemo(
    () => audioSections.map((s) => s.audioDurationMs ?? 0),
    [audioSections],
  );

  const { currentSectionIndex, setCurrentSectionIndex } = useAutoScroll(
    containerRef,
    audioPlayer,
    sectionDurations,
    settingsKey,
    scrollContainerRef,
  );

  // Follower: should audio play locally?
  const followerPlaysAudio = sessionSettings.musicDevice === 'follower';

  const handleFollowerPlay = useCallback(() => {
    if (followerPlaysAudio) {
      audioPlayer.play();
    }
  }, [audioPlayer, followerPlaysAudio]);

  const handleFollowerPause = useCallback(() => {
    audioPlayer.pause();
  }, [audioPlayer]);

  const handleFollowerSeek = useCallback(
    (_timeMs: number, sectionIndex: number) => {
      audioPlayer.seekToSection(sectionIndex);
    },
    [audioPlayer],
  );

  const handleFollowerAdjustRate = useCallback(
    (rate: number) => {
      audioPlayer.setPlaybackRate(rate);
    },
    [audioPlayer],
  );

  const followerSync = useFollowerSync({
    socket,
    sessionId,
    onPlay: handleFollowerPlay,
    onPause: handleFollowerPause,
    onSeek: handleFollowerSeek,
    onAdjustRate: handleFollowerAdjustRate,
  });

  const debugData = useFollowDebug(
    {
      driftMs: followerSync.driftMs,
      connectionState: followerSync.connectionState,
      driverStatus: followerSync.driverStatus,
      clockOffset: followerSync.clockOffset,
      clockRtt: followerSync.clockRtt,
      heartbeatCount: followerSync.heartbeatCount,
      lastHeartbeatMs: followerSync.lastHeartbeatMs,
      playbackRate: followerSync.playbackRate,
      currentSectionIndex,
      sectionCount: sections.length,
      transportLog: followerSync.transportLog,
    },
    debugOpen,
  );

  useEffect(() => {
    function handleDebugKey(e: KeyboardEvent) {
      if (e.key === 'd' && e.shiftKey && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setDebugOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', handleDebugKey);
    return () => document.removeEventListener('keydown', handleDebugKey);
  }, []);

  useEffect(() => {
    if (!hasAudio) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (
        e.code === 'Space' &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        if (countdown.state === 'countdown') {
          countdown.skip();
        } else if (audioPlayer.isPlaying) {
          audioPlayer.pause();
        } else {
          countdown.start();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasAudio, audioPlayer, countdown]);

  const theme = { colors: darkTheme };

  if (sections.length === 0) {
    return (
      <ThemeProvider theme={theme}>
        <div
          css={css`
            min-height: 100vh;
            background: ${darkTheme.background};
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: ${darkTheme.textSecondary};
            font-size: 18px;
            gap: ${spacing.md};
          `}
        >
          <p>No sections to display</p>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div
        css={css`
          height: 100vh;
          background: ${darkTheme.background};
          color: ${darkTheme.text};
          position: relative;
        `}
      >
        <div
          ref={scrollContainerRef}
          data-testid="teleprompter-scroll-container"
          css={css`
            height: 100%;
            overflow-y: auto;
            ${settings.mirrorHorizontal || settings.mirrorVertical
              ? `transform: ${[settings.mirrorHorizontal && 'scaleX(-1)', settings.mirrorVertical && 'scaleY(-1)'].filter(Boolean).join(' ')};`
              : ''}
          `}
        >
          {debugOpen && debugData && (
            <DebugConsole data={debugData} onClose={() => setDebugOpen(false)} />
          )}
          <div data-testid="teleprompter-content">
            <div
              ref={containerRef}
              css={css`
                max-width: ${settings.lineWidth}em;
                margin: 0 auto;
                padding: 0 ${spacing.md};
                font-size: ${settings.fontSize}px;

                ${mq.sm} {
                  padding: 0 ${spacing.lg};
                }

                ${mq.lg} {
                  padding: 0 ${spacing.xl};
                }
              `}
            >
              <div
                css={css`
                  height: 50vh;
                `}
              />
              {sections.map((section, index) => (
                <SectionDisplay
                  key={section.position}
                  title={section.title}
                  content={section.content}
                  position={section.position}
                  isCurrent={index === currentSectionIndex}
                  showDivider={index > 0}
                  onClick={() => setCurrentSectionIndex(index)}
                  fontSize={settings.fontSize}
                  speakerNotes={section.speakerNotes}
                />
              ))}
              <div
                css={css`
                  height: max(50vh, 120px);
                `}
              />
            </div>
          </div>
        </div>
        <TeleprompterControls
          currentSection={currentSectionIndex}
          sectionCount={sections.length}
          volume={settings.musicVolume}
          onVolumeChange={setMusicVolume}
          onSettingsOpen={() => setSettingsOpen(true)}
        />
        <SettingsPanel
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          settings={settings}
          onFontSizeChange={setFontSize}
          onLineWidthChange={setLineWidth}
          onScrollSpeedChange={setScrollSpeed}
          onMusicVolumeChange={setMusicVolume}
          onAutoScrollChange={setAutoScroll}
          onMirrorHorizontalChange={setMirrorHorizontal}
          onMirrorVerticalChange={setMirrorVertical}
          onReset={resetDefaults}
          connectionStatus={
            followerSync.connectionState === 'connected' ? 'Connected' : 'Disconnected'
          }
          debugOpen={debugOpen}
          onDebugToggle={() => setDebugOpen((prev) => !prev)}
        />
        {hasAudio && (
          <CountdownOverlay
            state={countdown.state}
            count={countdown.count}
            onSkip={countdown.skip}
          />
        )}
        {hasAudio && !audioUnlocked && audioPlayer.isReady && audioPlayer.getAudioElements() && (
          <IOSAudioUnlock
            audioElements={audioPlayer.getAudioElements()!}
            onUnlocked={() => setAudioUnlocked(true)}
          />
        )}
        {socket && (
          <SyncStatusIndicator
            driftMs={followerSync.driftMs}
            connectionState={followerSync.connectionState}
            driverStatus={followerSync.driverStatus}
          />
        )}
      </div>
    </ThemeProvider>
  );
}
